import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { RowListSkeleton } from "@/components/RowListSkeleton";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { ListDetail } from "@/components/ListDetail";
import { AlertList } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import type { Filterdescriptor } from "@/components/ui/data-table-filter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { datumKort } from "@/features/wedstrijden/datum";
import {
  useWedstrijddagen,
  type Seizoen,
  type Wedstrijddag,
} from "@/features/wedstrijden/useWedstrijden";
import { Versies } from "./Versies";
import { WedstrijdboekjeForm } from "./WedstrijdboekjeForm";
import {
  useCreateWedstrijdboekje,
  useDeleteWedstrijdboekje,
  useUpdateWedstrijdboekje,
  useWedstrijdboekje,
  useWedstrijdboekjes,
  type OngedekteDag,
  type Wedstrijdboekje,
} from "./useWedstrijdboekjes";

const helper = createColumnHelper<typeof dataTableFeatures, Wedstrijdboekje>();

/** The version count is the one thing worth seeing unopened: none means never published. */
const kolommen = (t: TFunction<"wedstrijdboekjes">) =>
  helper.columns([
    helper.accessor((boekje) => `${boekje.name} ${boekje.titel}`, {
      id: "boekje",
      header: t("lijst.naam"),
      cell: (info) => (
        <span>
          <span className="font-medium">{info.row.original.name}</span>
          <span className="block text-xs text-muted-foreground">
            {info.row.original.titel}
          </span>
        </span>
      ),
    }),
    helper.accessor((boekje) => boekje.generaties.length, {
      id: "versies",
      header: t("lijst.versies"),
      cell: (info) =>
        info.row.original.generaties.length === 0 ? (
          <span className="text-xs text-muted-foreground">
            {t("lijst.geenVersies")}
          </span>
        ) : (
          <span>{info.row.original.generaties.length}</span>
        ),
    }),
  ]);

/** §3.2. The seizoen is fixed by the URL: you arrive from the seizoen you chose. */
export function Wedstrijdboekjes({
  seizoen,
  boekjeId,
  onKies,
}: {
  seizoen: Seizoen;
  boekjeId: string | undefined;
  onKies: (id?: string) => void;
}) {
  const { t } = useTranslation("wedstrijdboekjes");
  const { data: overzicht, isPending } = useWedstrijdboekjes(seizoen.id);
  const { data: dagen } = useWedstrijddagen(seizoen.id);
  const boekje = overzicht?.boekjes.find((b) => b.id === boekjeId);

  const columns = kolommen(t);

  // Held whole and local (#194): the boekje itself already has the search box above, so the one
  // discrete filter worth adding is the count column search cannot reach - a boekje search would
  // duplicate what "Zoek op naam" already does.
  const descriptors = useMemo<Filterdescriptor<Wedstrijdboekje>[]>(
    () => [
      {
        id: "versies",
        label: t("lijst.filterVersies"),
        kind: "schakelaar",
        past: (boekje) => boekje.generaties.length === 0,
      },
    ],
    [t],
  );

  return (
    <>
      {overzicht && overzicht.ongedekteDagen.length > 0 && (
        <OngedekteDagen dagen={overzicht.ongedekteDagen} />
      )}

      <Toolbar>
        <NieuwWedstrijdboekje seizoen={seizoen} onAangemaakt={onKies} />
      </Toolbar>

      <ListDetail
        selected={Boolean(boekje)}
        backLabel={t("lijst.terug")}
        onBack={() => onKies()}
        list={
          <WedstrijdboekjesLijst
            isPending={isPending}
            columns={columns}
            boekjes={overzicht?.boekjes ?? []}
            selectedId={boekje?.id}
            onSelect={onKies}
            t={t}
            descriptors={descriptors}
            exportLink={exportHref(
              "/api/beheer/seizoenen/{seizoenId}/wedstrijdboekjes/export",
              { path: { seizoenId: seizoen.id } },
            )}
          />
        }
        detail={
          boekje ? (
            // Keyed on the id: the form inside is state seeded from the record, so choosing
            // another boekje resets it by remounting rather than by an effect that would also
            // fire on every poll while a version is being made.
            <WedstrijdboekjeDetail
              key={boekje.id}
              seizoen={seizoen}
              boekjeId={boekje.id}
              dagen={dagen ?? []}
              onVerwijderd={() => onKies()}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("lijst.kies")}
                </p>
              </CardContent>
            </Card>
          )
        }
      />
    </>
  );
}

/**
 * 2026's boekje was reissued on 29-05 because three dagen appeared after publication and nothing
 * said so (ADR-0036). A warning, not info: there is something to do, or to decide not to.
 */
function OngedekteDagen({ dagen }: { dagen: OngedekteDag[] }) {
  const { t } = useTranslation("wedstrijdboekjes");

  return (
    <AlertList
      tone="warning"
      className="mb-4"
      title={t("ongedekt.titel", { count: dagen.length })}
      description={t("ongedekt.uitleg")}
    >
      {/* A list, not a paragraph: seventeen pairs strung together is a wall nobody scans. A dag
        with no host says so rather than falling back to its niveau, which would read as the
        same kind of row. */}
      <ul className="list-disc space-y-0.5 ps-5">
        {dagen.map((dag) => (
          <li key={dag.id}>
            {datumKort(dag.datum)} ·{" "}
            {dag.organiserendKorpsNaam ?? t("ongedekt.zonderKorps")}
          </li>
        ))}
      </ul>
    </AlertList>
  );
}

/** Own component so swapping the skeleton for real data doesn't add a branch to the page shell. */
function WedstrijdboekjesLijst({
  isPending,
  columns,
  boekjes,
  selectedId,
  onSelect,
  t,
  descriptors,
  exportLink,
}: {
  isPending: boolean;
  columns: ReturnType<typeof kolommen>;
  boekjes: Wedstrijdboekje[];
  selectedId: string | undefined;
  onSelect: (id?: string) => void;
  t: TFunction<"wedstrijdboekjes">;
  descriptors: Filterdescriptor<Wedstrijdboekje>[];
  exportLink: string;
}) {
  return (
    <Card>
      {/* No CardHeader: the page title above both panes already says the noun. */}
      <CardContent className="pt-6">
        {isPending ? (
          <DataTableSkeleton
            label={t("lijst.label")}
            columns={columns}
            rows={3}
            search
            twoLine={["boekje"]}
          />
        ) : (
          <DataTable
            label={t("lijst.label")}
            data={boekjes}
            columns={columns}
            getRowId={(row) => row.id}
            selectedId={selectedId}
            onSelect={onSelect}
            searchPlaceholder={t("lijst.zoek")}
            empty={t("lijst.leeg")}
            exportHref={exportLink}
            filters={{ descriptors }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function NieuwWedstrijdboekje({
  seizoen,
  onAangemaakt,
}: {
  seizoen: Seizoen;
  onAangemaakt: (id: string) => void;
}) {
  const { t } = useTranslation("wedstrijdboekjes");
  const { data: dagen } = useWedstrijddagen(seizoen.id);
  const create = useCreateWedstrijdboekje(seizoen.id);
  const [open, setOpen] = useState(false);

  function wissel(volgende: boolean) {
    setOpen(volgende);
    if (!volgende) create.reset();
  }

  return (
    <Dialog open={open} onOpenChange={wissel}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          {t("lijst.toevoegen")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("lijst.toevoegen")}</DialogTitle>
          <DialogDescription>
            {t("lijst.aanSeizoen", { naam: seizoen.name })}
          </DialogDescription>
        </DialogHeader>

        {/* Keyed on whether the modal is open, so every visit starts on an empty form rather
            than on what was abandoned last time. */}
        <WedstrijdboekjeForm
          key={String(open)}
          id="nieuw-boekje"
          dagen={dagen ?? []}
          submitLabel={t("lijst.submitToevoegen")}
          pending={create.isPending}
          error={create.error}
          onSubmit={(velden) =>
            create.mutate(velden, {
              onSuccess: (nieuw) => {
                wissel(false);
                onAangemaakt(nieuw.boekje.id);
              },
            })
          }
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reads the boekje again rather than reusing the row: it is the only place that answers whether
 * anything changed, which costs a full read of its pages.
 */
function WedstrijdboekjeDetail({
  seizoen,
  boekjeId,
  dagen,
  onVerwijderd,
}: {
  seizoen: Seizoen;
  boekjeId: string;
  dagen: Wedstrijddag[];
  onVerwijderd: () => void;
}) {
  const { t } = useTranslation(["wedstrijdboekjes", "common"]);
  const { data: boekje, isPending, isError } = useWedstrijdboekje(boekjeId);
  const update = useUpdateWedstrijdboekje(seizoen.id);
  const remove = useDeleteWedstrijdboekje(seizoen.id);

  /* A boekje can vanish under you. Without this branch an error left `data` undefined and
    the skeleton never resolved. */
  if (isError) {
    return (
      <Card>
        <CardContent className="space-y-3 pt-6">
          <p className="text-sm text-muted-foreground">{t("detail.weg")}</p>
          <Button variant="outline" onClick={onVerwijderd}>
            {t("lijst.terug")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isPending || !boekje) {
    return (
      <Card>
        <CardContent className="pt-6">
          <RowListSkeleton rows={3} />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{boekje.boekje.name}</CardTitle>
          <p className="text-sm text-muted-foreground">{boekje.boekje.titel}</p>
        </CardHeader>
        <CardContent>
          <WedstrijdboekjeForm
            id="boekje"
            boekje={boekje.boekje}
            dagen={dagen}
            submitLabel={t("form.opslaan")}
            pending={update.isPending}
            error={update.error}
            onSubmit={(velden) =>
              update.mutate({ ...velden, id: boekje.boekje.id })
            }
          />
        </CardContent>
      </Card>

      <Versies seizoenId={seizoen.id} boekje={boekje} />

      <VerwijderBoekje
        boekje={boekje.boekje}
        remove={remove}
        onVerwijderd={onVerwijderd}
      />
    </div>
  );
}

/** Own card so the confirm-delete wiring doesn't add branches to the detail pane above it. */
function VerwijderBoekje({
  boekje,
  remove,
  onVerwijderd,
}: {
  boekje: Wedstrijdboekje;
  remove: ReturnType<typeof useDeleteWedstrijdboekje>;
  onVerwijderd: () => void;
}) {
  const { t } = useTranslation(["wedstrijdboekjes", "common"]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("verwijderen.titel")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t("verwijderen.uitleg")}
        </p>
        <FormActions>
          {remove.error && (
            <p className="text-sm text-destructive">{remove.error.message}</p>
          )}
          <ConfirmDelete
            trigger={
              <Button variant="destructive" disabled={remove.isPending}>
                {remove.isPending
                  ? t("common:actions.busy")
                  : t("common:actions.delete")}
              </Button>
            }
            title={t("verwijderen.vraag", { naam: boekje.name })}
            description={t("verwijderen.beschrijving")}
            confirmLabel={t("verwijderen.confirm")}
            onConfirm={() =>
              remove.mutate(boekje.id, { onSuccess: onVerwijderd })
            }
          />
        </FormActions>
      </CardContent>
    </Card>
  );
}
